import {useEffect, useState, type FormEvent} from 'react';
import {AwAppShell, AwButton, AwErrorState, AwField, AwLoadingState, AwPageHeader, AwPanel, AwStatusPill} from '@ascension-tracker/ascension-ui';
import {api, ApiClientError} from '../api.js';
import {useAsync} from '../hooks/useAsync.js';

type OwnProfile = {id: string; email: string; displayName: string; systemRole: string};

export function Profile() {
  const profile = useAsync(() => api<OwnProfile>('/me'), []);
  const [displayName, setDisplayName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile.data) setDisplayName(profile.data.displayName);
  }, [profile.data]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setSaved(false);
    setSaving(true);
    try {
      await api('/me', {method: 'PATCH', body: JSON.stringify({displayName, currentPassword: currentPassword || undefined, newPassword: newPassword || undefined})});
      setCurrentPassword('');
      setNewPassword('');
      setSaved(true);
      profile.reload();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  }

  return (
    <AwAppShell>
      <AwPageHeader eyebrow="Personal Workspace" title="Profile Settings">
        <p>Update your display name and password.</p>
      </AwPageHeader>
      {profile.error && <AwErrorState>{profile.error}</AwErrorState>}
      {!profile.data ? <AwLoadingState/> : (
        <AwPanel>
          <form className="project-form" onSubmit={save}>
            {saved && <AwStatusPill state="completed">Profile updated</AwStatusPill>}
            {error && <AwErrorState>{error}</AwErrorState>}
            <AwField id="profile-email" label="Email" value={profile.data.email} disabled readOnly/>
            <AwField id="profile-display-name" label="Display Name" value={displayName} onChange={event => setDisplayName(event.target.value)} required minLength={2}/>
            <div className="project-form__row">
              <AwField id="profile-current-password" label="Current Password" type="password" placeholder="Required to change password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} autoComplete="current-password"/>
              <AwField id="profile-new-password" label="New Password" type="password" placeholder="Leave blank to keep current password" value={newPassword} onChange={event => setNewPassword(event.target.value)} autoComplete="new-password" minLength={8}/>
            </div>
            <div className="project-form__actions">
              <AwButton type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</AwButton>
            </div>
          </form>
        </AwPanel>
      )}
    </AwAppShell>
  );
}
